// ---------------------------------------------------------------------------
// ABLATION SWITCHES — for finding out which rule is actually in charge.
//
// Morgan, Aug 22: "Do not tune any constants yet. I want to identify which
// rule is actually controlling the outcome." And on primaryPenalty: "Don't
// remove it yet; just isolate its effect."
//
// EVERY FLAG DEFAULTS TO FALSE, which is exactly today's behaviour. Each read
// site is guarded so that with no flag set the code path is byte-for-byte the
// production one. Nothing here is a routing rule; it is a way of asking what
// the routing rules are doing.
// ---------------------------------------------------------------------------

export interface AblationFlags {
  /** Zero out arterialPenalty — the (highwayPct-5)*0.70 charge. The backroad
   *  boost still applies, so this isolates the penalty from the reward. */
  motorwayScoreOff: boolean;
  /** Zero out primaryPenalty — the SECOND charge on highwayPct, whose stated
   *  purpose ("covers motorway+trunk+primary") stopped being true at #139. */
  primaryPenaltyOff: boolean;
  /** Disable THE SUPREME RULE promotion (<=25% main road AND within 60s of
   *  fastest becomes the Pleasant card automatically). */
  supremeRuleOff: boolean;
  /** Disable the separate short-trip ranker in minutesRule.ts entirely. */
  minutesRuleOff: boolean;
  /** Zero out namedArterialPenalty — fires at >=40% named arterial, up to -50,
   *  the largest single penalty in the file. Named arterials also feed
   *  highwayPct, so it shares a root with arterial/primary/calm. */
  namedArterialPenaltyOff: boolean;
  /** Stop the `calm` base component from discriminating on highwayPct. The
   *  term is held CONSTANT at its maximum rather than deleted, so the score
   *  stays on the same scale and only the discrimination is removed. */
  calmHighwayTermOff: boolean;
  /** Remove the hard CEILING at scoring.ts:1272 — when the user avoids
   *  highways and a route is >=25% highway, `pleasant` is truncated to 45
   *  (20 above 50%). A truncation, not a subtraction: a route can be carrying
   *  +45 ocean and +28 scenic and still be slammed to 45. */
  highwayCapOff: boolean;
  /** Remove the component FLOOR at scoring.ts:2348, which lifts pleasant to
   *  55% (or 45%) of the strongest component. Suspected — not established —
   *  of neutralising the entire highway family: a route driven to -37 by five
   *  penalties is lifted to 23 regardless, so turning any of them off changes
   *  nothing. This switch is how that claim gets falsified. */
  componentFloorOff: boolean;
  /** Replace the raw `epicHighwayMeters > 1000` bypasses with the
   *  proportional test the curated-road path already uses (the curated stretch
   *  must be >=25% of the trip). Morgan, Jul 15: "it should not be included if
   *  it's not actually scenic for MOST of the trip." */
  epicProportional: boolean;
}

const OFF: AblationFlags = {
  motorwayScoreOff: false,
  primaryPenaltyOff: false,
  supremeRuleOff: false,
  minutesRuleOff: false,
  namedArterialPenaltyOff: false,
  calmHighwayTermOff: false,
  highwayCapOff: false,
  componentFloorOff: false,
  epicProportional: false,
};

export const ABLATION: AblationFlags = { ...OFF };

/** Run `fn` with some flags set, then restore. Synchronous on purpose: the
 *  scoring path is synchronous, and an async version would let two variants
 *  interleave and silently contaminate each other's results. */
export function withAblation<T>(flags: Partial<AblationFlags>, fn: () => T): T {
  const saved = { ...ABLATION };
  Object.assign(ABLATION, flags);
  try {
    return fn();
  } finally {
    Object.assign(ABLATION, saved);
  }
}

export function resetAblation(): void {
  Object.assign(ABLATION, OFF);
}

/** The five variants Morgan asked for, plus production. */
export const VARIANTS: Array<{ name: string; flags: Partial<AblationFlags>; asks: string }> = [
  { name: "production", flags: {}, asks: "what ships today" },
  { name: "motorway-score-off", flags: { motorwayScoreOff: true },
    asks: "does the (highwayPct-5)*0.70 charge decide the winner?" },
  { name: "primary-penalty-off", flags: { primaryPenaltyOff: true },
    asks: "what does the SECOND charge on the same variable change on its own?" },
  { name: "named-arterial-off", flags: { namedArterialPenaltyOff: true },
    asks: "what does the largest single penalty (-50 cap) change on its own?" },
  { name: "calm-highway-term-off", flags: { calmHighwayTermOff: true },
    asks: "does the FIRST charge on highwayPct, inside the base sum, matter?" },
  { name: "highway-cap-off", flags: { highwayCapOff: true },
    asks: "is the hard truncation to 45 discarding scenery a route already earned?" },
  { name: "ALL-highway-rooted-off",
    flags: { motorwayScoreOff: true, primaryPenaltyOff: true,
             namedArterialPenaltyOff: true, calmHighwayTermOff: true,
             highwayCapOff: true },
    asks: "do five individually-small penalties add up to one large bias?" },
  { name: "COMPONENT-FLOOR-off", flags: { componentFloorOff: true },
    asks: "is the floor erasing the highway penalties before they can decide anything?" },
  { name: "floor-off + ALL-highway-off",
    flags: { componentFloorOff: true, motorwayScoreOff: true, primaryPenaltyOff: true,
             namedArterialPenaltyOff: true, calmHighwayTermOff: true, highwayCapOff: true },
    asks: "with the floor gone, do the highway penalties finally move winners?" },
  { name: "supreme-rule-off", flags: { supremeRuleOff: true },
    asks: "is the <=25%/60s promotion picking the winner before scoring matters?" },
  { name: "minutes-rule-off", flags: { minutesRuleOff: true },
    asks: "is the separate short-trip ranker fighting the main one?" },
  { name: "epic-proportional", flags: { epicProportional: true },
    asks: "does 0.62mi of a great drive exempt routes it shouldn't?" },
];
